import { FormProvider } from '@ariakit/react'
import { ComponentProps } from 'react'
import styled from 'styled-components'

import { Button, Spacer } from '@syconium/little-miss-figgy'

import {
  ProductCatalogProduct,
  ProductCatalogVariant,
  ProductCatalogVariantGroup,
} from '../../../../../__generated__/graphql/catalog/graphql'
import {
  VariantSelectionForm,
  VariantSelectionFormPurchasingSubmit,
  VariantSelectionSizeSelect,
  useVariantSelectionForm,
} from '../../../../../app/_components/forms/VariantSelectionForm'
import { NextLink } from '../../../../../app/_components/navigation/NextLink'
import { useCart } from '../../../../../app/_providers/CartProvider.client'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { useUpdateQueryParams } from '../../../../../lib/adapters/next-routing-service/useUpdateQueryParam'
import { trackEvent } from '../../../../../lib/analytics'
import { useRiseAiScript } from '../../../../../lib/hooks/useRiseAiScript'
import { CartErrorMessage } from '../../../../atoms/CartErrorMessage/CartErrorMessage'
import { useIsPhysicalGiftCard } from '../hooks/useIsGiftCard'
import { usePdpServerSideProps } from '../PdpServerSidePropsProvider'
import { Description, ErrorMessageContainer } from '../ProductHighlights/ProductHighlights.styles'
import { DetailsSectionSidePaddedSection } from '../styles'

import { BoldText, Separator, UnderlinedLink } from './styles'

const trackingCategory = 'product details'
const digitalGiftCardLinkTo = '/products/figs-gift-card'
const giftCardTermsLinkTo = '/pages/gift-card-terms'
const helpCenterHref = 'https://help.wearfigs.com'
const physicalGiftCardLinkTo = '/products/figs-physical-gift-card'

export type GiftCardDetailsProps = {
  product: {
    handle: ProductCatalogProduct['handle']
    options: {
      sizes: Array<{
        size: {
          id: string
          displayValue: string
        }
        outOfStock: boolean
      }>
    }
  } & ComponentProps<typeof VariantSelectionForm>['product']
  variantGroup: {
    title: ProductCatalogVariantGroup['title']
    description: ProductCatalogVariantGroup['description']
  } & NonNullable<Parameters<typeof useRiseAiScript>[0]>['variantGroup'] &
    ComponentProps<typeof VariantSelectionForm>['variantGroup']
  variant: Pick<ProductCatalogVariant, 'fit' | 'size' | 'shopifyProductId'> &
    NonNullable<Parameters<typeof useRiseAiScript>[0]>['variant'] &
    ComponentProps<typeof VariantSelectionForm>['selectedVariant']
}

export const GiftCardDetails = ({ product, variantGroup, variant }: GiftCardDetailsProps) => {
  const { giftCard: giftCardStrings } = useTranslation()
  const isPhysicalGiftCard = useIsPhysicalGiftCard({
    productGroupHandle: product.handle,
  })

  return (
    <DetailsSectionSidePaddedSection>
      <Spacer height={20} />
      <FormProvider>
        <GiftCardDetailsForm product={product} variant={variant} variantGroup={variantGroup} />
      </FormProvider>
      <Spacer height={16} />
      {variantGroup.description ? (
        <Description
          {...trackEvent({
            category: trackingCategory,
            action: 'snapshot',
            label: 'description',
            value: variantGroup.title,
          })}
        >
          {variantGroup.description}
        </Description>
      ) : null}
      <Spacer height={16} />
      {isPhysicalGiftCard ? (
        <>
          <Description>
            {giftCardStrings.lookingForDigital}{' '}
            <NextLink href={digitalGiftCardLinkTo}>
              <UnderlinedLink>{giftCardStrings.here}</UnderlinedLink>
            </NextLink>
          </Description>
        </>
      ) : (
        <>
          <Description>
            {giftCardStrings.lookingForPhysical}{' '}
            <NextLink href={physicalGiftCardLinkTo}>
              <UnderlinedLink>{giftCardStrings.here}</UnderlinedLink>
            </NextLink>
          </Description>
          <Separator />
          <Description>
            <BoldText>{giftCardStrings.wantToGiftSomeone}</BoldText> {giftCardStrings.giftSomeone}
          </Description>
          &nbsp;
          <Description>
            <BoldText>{giftCardStrings.note}</BoldText> {giftCardStrings.giftSomeoneNote}
          </Description>
          <Separator />
          <Description>
            <BoldText>{giftCardStrings.wantToGiftYourself}</BoldText> {giftCardStrings.giftYourself}
          </Description>
        </>
      )}
      <Spacer height={16} />
      <Description>
        {giftCardStrings.anyQuestions}{' '}
        <NextLink href={helpCenterHref}>
          <UnderlinedLink>{giftCardStrings.helpCenter}</UnderlinedLink>
        </NextLink>
      </Description>
      <Spacer height={16} />
      <Description>
        {giftCardStrings.seeOurTOCs}{' '}
        <NextLink href={giftCardTermsLinkTo}>
          <UnderlinedLink>{giftCardStrings.here}</UnderlinedLink>
        </NextLink>
      </Description>
    </DetailsSectionSidePaddedSection>
  )
}

const GiftCardDetailsForm = ({ variant, variantGroup, product }: GiftCardDetailsProps) => {
  const { giftCard: giftCardStrings } = useTranslation()
  const isPhysicalGiftCard = useIsPhysicalGiftCard({
    productGroupHandle: product.handle,
  })
  const { formStore } = useVariantSelectionForm({
    defaultValues: {
      initialColor: variantGroup.color?.id ?? '',
      initialFit: variant.fit?.id,
      initialSize: variant.size?.id,
    },
  })
  const { status } = usePdpServerSideProps()
  const updateQueryParams = useUpdateQueryParams()
  const { errorMessage } = useCart()

  const giftMyselfText = isPhysicalGiftCard
    ? giftCardStrings.giftMyself
    : giftCardStrings.emailMyself

  return (
    <VariantSelectionForm
      store={formStore}
      product={product}
      selectedVariant={variant}
      variantGroup={variantGroup}
      trackingCategory={trackingCategory}
      loading={status === 'pending'}
    >
      <VariantSelectionSizeSelect
        onChange={size => {
          updateQueryParams({
            size,
          })
        }}
      />
      <StyledAddToBagButton
        className='Rise-add-to-cart-button'
        $includeBottomMargin={!isPhysicalGiftCard}
        isPrimaryCtaForPageView
      >
        {giftMyselfText}
      </StyledAddToBagButton>
      {isPhysicalGiftCard ? null : (
        <DigitalGiftCardGiftingButton
          variant={variant}
          variantGroup={variantGroup}
          product={product}
        />
      )}
      {errorMessage && (
        <ErrorMessageContainer>
          <CartErrorMessage errorMessage={errorMessage} scrollToError />
        </ErrorMessageContainer>
      )}
    </VariantSelectionForm>
  )
}

const DigitalGiftCardGiftingButton = ({ variant, variantGroup, product }: GiftCardDetailsProps) => {
  const { giftCard: giftCardStrings } = useTranslation()

  useRiseAiScript({
    variant,
    variantGroup,
    product,
  })

  return (
    <Button
      {...trackEvent({
        action: 'rise add to cart',
        category: trackingCategory,
        label: giftCardStrings.emailSomeoneElse,
      })}
      aria-disabled={false}
      className='gwbutton'
      data-testid='rise-add-to-bag-button'
      allCaps={true}
      block
      size='large'
      variant='primary'
    >
      {giftCardStrings.emailSomeoneElse}
    </Button>
  )
}

// Rise AI script is adding 15px bottom margin and 15px right margin.... so adding it here to prevent CLS
const StyledAddToBagButton = styled(VariantSelectionFormPurchasingSubmit)<{
  $includeBottomMargin: boolean
}>`
  ${o => (o.$includeBottomMargin ? 'margin-bottom: 15px;' : null)};
  margin-top: 15px;
  margin-right: 0px !important;
`
